import { useState } from 'react';
import type { ReactNode } from 'react';
import { CLIENT_DURATION_MAX_MINUTES, CLIENT_DURATION_MIN_MINUTES } from '@yourstore/domain';
import { useActions, useConnection, useLive, useRuntime } from './context';
import { CreditPanel } from './Credit';
import { Permissions } from './Permissions';
import { ActionGlyph } from './brand';
import { useActionIcon, useRemote } from './useRemote';
import { RefundSheet } from './Refund';
import { formatMoney } from '../mirror/time';
import { SettlementList } from '../wire';
import type { ClientDetail, WireIdentity } from '../wire';
import { useLocale, useT } from '../messages';

/**
 * Clients — search, detail, edit, archive.
 *
 * The screen that makes the staff/partner asymmetry visible. `internal`
 * preferences are shown here and marked as staff-only, and they exist nowhere on
 * the partner surface: obligation G7, enforced in the database by the
 * `provider_api` role having no `SELECT` on `client_preferences` at all. The
 * badge is not the enforcement — it is the reminder to the person typing that
 * what they write in that box stays behind the counter.
 *
 * Archive rather than delete, for the reason `core-api` gives: `appointments`
 * references clients with `ON DELETE RESTRICT`, and the right to be forgotten is
 * anonymization, which keeps the ledger honest (§7.6 — anonymize, never
 * falsify).
 */
export function Clients(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const action = useActionIcon();

  const clients = useLive(() => runtime.mirror.clients(search), [], [search]);
  const detail = useLive(
    async () => (selected === null ? null : ((await runtime.mirror.client(selected)) ?? null)),
    null,
    [selected],
  );

  return (
    <section className="view view--split">
      <div className="pane pane--list">
        <div className="view__header">
          <input
            className="search"
            placeholder={t('clients.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className={`button button--primary${action.icon === 'heart' ? ' button--heart' : ''}`}
            onClick={() => setAdding(true)}
          >
            <ActionGlyph icon={action.icon} />
            {t('clients.new')}
          </button>
        </div>

        {clients.length === 0 ? (
          <p className="empty">
            {t('clients.noneMatch')}
            {action.icon === 'heart'
              ? t('clients.noneMatchHeart', { button: action.buttonName })
              : null}
          </p>
        ) : (
          <ul className="client-list">
            {clients.map((c) => (
              <li key={c.client_id}>
                <button
                  type="button"
                  className={`client-list__item${selected === c.client_id ? ' is-selected' : ''}`}
                  onClick={() => setSelected(c.client_id)}
                >
                  <strong>{c.display_name}</strong>
                  <small>
                    {c.identities[0]?.identity ?? t('clients.noContact')} ·{' '}
                    {t('clients.visits', { count: c.visit_count })}
                  </small>
                  {c.archived ? <span className="badge">{t('clients.archived')}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pane pane--detail">
        {detail === null ? (
          <p className="empty">{t('clients.pickSomeone')}</p>
        ) : (
          <ClientDetailPane client={detail} />
        )}
      </div>

      {adding ? <NewClientSheet onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function ClientDetailPane({ client }: { client: ClientDetail }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const session = useLive(() => runtime.mirror.session(), null);
  const currency = session?.tenant.currency ?? 'USD';
  const hasCredit = (session?.modules ?? []).includes('credit');
  // No `sales`, no settlements to list — the tab is not drawn rather than shown
  // empty, which is §3.2's rule: we do not advertise what a tenant has not
  // bought.
  const canSell = (session?.modules ?? []).includes('sales');
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(client.display_name);
  const [notes, setNotes] = useState(client.notes ?? '');
  const [label, setLabel] = useState('');
  const [internal, setInternal] = useState(false);
  const [category, setCategory] = useState<'preference' | 'allergy'>('preference');

  return (
    <article className="client-detail">
      <header>
        <h1>{client.display_name}</h1>
        <div className="row-actions">
          <button type="button" className="link-button" onClick={() => setEditing((v) => !v)}>
            {editing ? t('common.close') : t('common.edit')}
          </button>
          <button
            type="button"
            className="link-button link-button--danger"
            onClick={() =>
              void actions.archiveClient({
                clientId: client.client_id,
                name: client.display_name,
                archived: !client.archived,
              })
            }
          >
            {client.archived ? t('common.restore') : t('clients.archive')}
          </button>
        </div>
      </header>

      {editing ? (
        <div className="editor">
          <label className="field">
            <span>{t('common.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('common.notes')}</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              void actions.updateClient({
                clientId: client.client_id,
                name: client.display_name,
                changes: { name, notes },
              });
              setEditing(false);
            }}
          >
            {t('common.save')}
          </button>
        </div>
      ) : null}

      <h2>{t('clients.contactHeading')}</h2>
      <ul className="identity-list">
        {client.identities.map((identity) => (
          <li key={`${identity.identity_kind}:${identity.identity}`}>
            <span>{identity.identity}</span>
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() =>
                void actions.removeIdentity({
                  clientId: client.client_id,
                  name: client.display_name,
                  identity,
                })
              }
            >
              {t('common.remove')}
            </button>
          </li>
        ))}
        {client.identities.length === 0 ? (
          <li className="muted">{t('clients.noIdentity')}</li>
        ) : null}
      </ul>
      <AddIdentity client={client} />

      {/* Directly under the numbers and the addresses, because that is what
          every one of them is about: what we may send to *this* phone, to
          *this* mailbox, and what she may do with the site herself. Three waves
          each put one tick here on the same day; `Permissions.tsx` is the one
          group they belong in and the argument for keeping the three *writes*
          separate. */}
      <Permissions client={client} />

      {hasCredit ? (
        <CreditPanel
          clientId={client.client_id}
          clientName={client.display_name}
          currency={currency}
        />
      ) : null}

      <ClientLengths client={client} />

      {canSell ? <ClientPurchases clientId={client.client_id} currency={currency} /> : null}

      <h2>{t('clients.preferencesHeading')}</h2>
      <ul className="preference-list">
        {client.preferences.map((p) => (
          <li key={p.preference_id} className={p.internal ? 'is-internal' : ''}>
            <div>
              <strong>{p.label}</strong>
              {p.detail === null ? null : <small>{p.detail}</small>}
            </div>
            <div className="row-actions">
              <span className={`badge badge--${p.category}`}>
                {p.category === 'allergy'
                  ? t('clients.categoryAllergy')
                  : t('clients.categoryPreference')}
              </span>
              {p.internal ? (
                <span className="badge badge--internal" title={t('clients.staffOnlyTitle')}>
                  {t('clients.staffOnly')}
                </span>
              ) : null}
              <button
                type="button"
                className="link-button link-button--danger"
                onClick={() =>
                  void actions.deletePreference({
                    clientId: client.client_id,
                    preferenceId: p.preference_id,
                    name: client.display_name,
                    label: p.label,
                  })
                }
              >
                {t('common.remove')}
              </button>
            </div>
          </li>
        ))}
        {client.preferences.length === 0 ? (
          <li className="muted">{t('clients.nothingNoted')}</li>
        ) : null}
      </ul>

      <div className="editor editor--inline">
        <input
          placeholder={t('clients.addPreference')}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value === 'allergy' ? 'allergy' : 'preference')}
        >
          {/* The `value` is the domain's enum and never moves; only the label
              a person reads is translated. */}
          <option value="preference">{t('clients.categoryPreference')}</option>
          <option value="allergy">{t('clients.categoryAllergy')}</option>
        </select>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={internal}
            onChange={(e) => setInternal(e.target.checked)}
          />
          <span>{t('clients.staffOnly')}</span>
        </label>
        <button
          type="button"
          className="button"
          disabled={label.trim() === ''}
          onClick={() => {
            void actions.addPreference({
              clientId: client.client_id,
              name: client.display_name,
              category,
              label: label.trim(),
              internal,
            });
            setLabel('');
            setInternal(false);
          }}
        >
          {t('common.add')}
        </button>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Her lengths (A1) and what she has bought (B3)                       */
/* ------------------------------------------------------------------ */

/**
 * **Her lengths** — how long each service takes this one guest
 * (`docs/lovespa-discovery.md` §2.1: *"your eyebrow wax takes me 15 minutes…
 * hers takes me 35. Only waxing varies."*).
 *
 * Read out of the **mirror**, because the row travels on the client detail
 * (`wire.ts`, `ClientDetail.durations`) rather than on a fetch of its own: the
 * booking sheet has to know she is a thirty-five in a tunnel, and a fact only
 * one screen can reach when there is signal is not the fact the owner thinks
 * she recorded.
 *
 * Written through the **outbox**, like every other write in this app, so
 * retiming a client standing in the doorway with no bars queues and replays.
 * Queued edits are overlaid on the mirrored list and marked — the Today
 * screen's optimistic-row rule, applied to a fact instead of to a booking. That
 * is a display-side choice and not a merge: nothing is written and nothing is
 * combined, and the moment the intent settles the real row is what draws.
 *
 * **Forgetting changes only what is offered next time.** Bookings already made
 * keep the span they were booked at — that span lives on the appointment and a
 * reschedule moves it rather than re-deriving it — and the panel says so, so
 * "forget" never reads as "rewrite her calendar".
 */
function ClientLengths({ client }: { client: ClientDetail }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const services = useLive(() => runtime.mirror.services(), []);
  const pending = useLive(() => runtime.outbox.pending(), []);
  const [adding, setAdding] = useState('');
  // Which length of `adding` — `''` is "however it is sold", the broad row.
  const [addingOption, setAddingOption] = useState('');
  const [addMinutes, setAddMinutes] = useState('');

  // `?? []` — a row this device wrote before the field existed is read back from
  // IndexedDB without it, whatever the type says (`wire.ts`, `ClientList`).
  const mirrored = client.durations ?? [];

  const queued = pending.filter(
    (entry) =>
      (entry.kind === 'duration.set' || entry.kind === 'duration.clear') &&
      entry.target['client_id'] === client.client_id,
  );
  /**
   * The queued edit about one row, if any — keyed by `(service, option)`,
   * because that is what the intent's target names and what the two write
   * routes are keyed by. A broad intent's target has no `option_id`; an option
   * row's names one. Matching on the pair is what keeps a queued Forget of the
   * ninety from painting the broad row as vanishing.
   */
  const queuedFor = (
    serviceId: string,
    optionId: string | null,
  ): (typeof queued)[number] | undefined =>
    queued.find(
      (entry) =>
        entry.target['service_id'] === serviceId &&
        (entry.target['option_id'] ?? null) === optionId,
    );

  /**
   * **One line per remembered length, not per service** (migration 0048).
   *
   * A guest can be remembered twice for one treatment: once broadly ("this
   * service however it is sold") and once per length she actually buys — the
   * two partial unique indexes on `client_service_durations` are exactly that
   * shape. Keying the list by service id alone would draw one of them and hide
   * the other, silently, which is the worst thing this panel could do about a
   * number that decides how long somebody is in a room.
   *
   * The queued overlay is keyed by the same pair, because the intent's target
   * names the option when there is one (`actions.setDuration`'s `optionId`).
   */
  const keyOf = (serviceId: string, optionId: string | null): string =>
    `${serviceId}|${optionId ?? ''}`;

  const subjects: { serviceId: string; optionId: string | null }[] = [
    ...mirrored.map((d) => ({ serviceId: d.service_id, optionId: d.option_id })),
    // A queued edit about a row with nothing mirrored yet — the first "she is a
    // 35" for a service, written in a tunnel — is drawn from the intent alone.
    ...queued
      .map((entry) => ({
        serviceId: entry.target['service_id'] ?? '',
        optionId: entry.target['option_id'] ?? null,
      }))
      .filter(
        ({ serviceId, optionId }) =>
          serviceId !== '' &&
          !mirrored.some((d) => d.service_id === serviceId && d.option_id === optionId),
      ),
  ];

  const rows = subjects
    .map(({ serviceId, optionId }) => {
      const row = mirrored.find((d) => d.service_id === serviceId && d.option_id === optionId);
      const service = services.find((s) => s.service_id === serviceId);
      const entry = queuedFor(serviceId, optionId);
      /**
       * **Which length of it she is remembered at** — "Custom Deluxe Facial ·
       * 90 minutes". The name comes off the mirrored row, or — for a row that
       * exists only as a queued intent, the picker's "105 on the ninety" in a
       * tunnel — off the mirrored catalog, which is the same name the server
       * will send back once the intent lands.
       */
      const catalogOption =
        optionId === null
          ? undefined
          : (service?.options ?? []).find((o) => o.option_id === optionId);
      const optionName = row?.option_name ?? catalogOption?.name ?? null;
      const name =
        optionName === null
          ? (row?.service_name ?? service?.name ?? serviceId)
          : t('clients.lengthOption', {
              service: row?.service_name ?? service?.name ?? serviceId,
              option: optionName,
            });
      const catalog =
        row?.catalog_duration_min ?? catalogOption?.duration_min ?? service?.duration_min ?? 0;
      const key = keyOf(serviceId, optionId);
      if (entry?.kind === 'duration.clear') {
        return { key, serviceId, optionId, name, catalog, minutes: null, pending: true };
      }
      const minutes =
        entry?.kind === 'duration.set'
          ? Number(entry.payload['duration_min'])
          : (row?.duration_min ?? null);
      return { key, serviceId, optionId, name, catalog, minutes, pending: entry !== undefined };
    })
    .filter((row) => row.minutes !== null || row.pending)
    .sort((a, b) => a.name.localeCompare(b.name));

  /**
   * **What the picker may still add**, per service: the broad row (`null`) and
   * one slot per active option — minus the rows she already has, mirrored or
   * queued, so the picker cannot make a duplicate. A service with no options
   * has one slot; one sold in three lengths has four, because "35 however it is
   * sold" and "105 on the ninety" are different facts and she may hold both.
   */
  const taken = new Set(subjects.map(({ serviceId, optionId }) => keyOf(serviceId, optionId)));
  const freeSlots = (s: (typeof services)[number]): (string | null)[] =>
    [null, ...(s.options ?? []).filter((o) => o.active).map((o) => o.option_id)].filter(
      (optionId) => !taken.has(keyOf(s.service_id, optionId)),
    );
  const addable = services.filter(
    (s) => s.active && !s.service_id.startsWith('__') && freeSlots(s).length > 0,
  );
  const addingService = services.find((s) => s.service_id === adding);
  const addingSlots = addingService === undefined ? [] : freeSlots(addingService);
  // A service sold one way has one slot and needs no second control — a
  // chooser with one value is a control that lies (the repo's rule).
  const showLengths = addingService !== undefined && (addingService.options ?? []).length > 0;

  return (
    <>
      <h2>{t('clients.lengthsHeading')}</h2>
      <ul className="length-list">
        {rows.map((row) => (
          <li key={row.key} className={row.pending ? 'is-pending' : ''}>
            <div>
              <strong>{row.name}</strong>
              <small>
                {row.minutes === null
                  ? t('clients.lengthForgotten', { catalog: row.catalog })
                  : t('clients.lengthHers', { minutes: row.minutes, catalog: row.catalog })}
              </small>
            </div>
            <div className="row-actions">
              {row.pending ? (
                <span className="badge badge--pending">{t('today.waitingToSend')}</span>
              ) : null}
              {/*
               * Edit and Forget on every row, **each targeting the row it sits
               * on**: an option row's verbs carry the option, so a Forget on
               * "Facial · 90 minutes" queues a clear of that row and leaves the
               * broad one where it was. The control does what it says, which is
               * the only reason it is drawn here at all — before the routes had
               * an option these two were withheld on option rows rather than
               * offered wrong.
               */}
              {row.minutes === null ? null : (
                <>
                  <LengthEditor
                    clientId={client.client_id}
                    clientName={client.display_name}
                    serviceId={row.serviceId}
                    optionId={row.optionId}
                    serviceName={row.name}
                    minutes={row.minutes}
                  />
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    onClick={() =>
                      void actions.clearDuration({
                        clientId: client.client_id,
                        clientName: client.display_name,
                        serviceId: row.serviceId,
                        ...(row.optionId === null ? {} : { optionId: row.optionId }),
                        serviceName: row.name,
                      })
                    }
                  >
                    {t('clients.lengthForget')}
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
        {rows.length === 0 ? <li className="muted">{t('clients.lengthsNone')}</li> : null}
      </ul>
      <p className="muted">{t('clients.lengthsNote')}</p>

      {addable.length === 0 ? null : (
        <div className="editor editor--inline">
          <select
            aria-label={t('clients.lengthAddService')}
            value={adding}
            onChange={(e) => {
              setAdding(e.target.value);
              // The first free slot of the new service: the broad row when she
              // has none, else the first length she is not yet remembered at.
              const next = services.find((s) => s.service_id === e.target.value);
              setAddingOption(next === undefined ? '' : (freeSlots(next)[0] ?? ''));
            }}
          >
            <option value="">{t('clients.lengthAddService')}</option>
            {addable.map((s) => (
              <option key={s.service_id} value={s.service_id}>
                {t('book.serviceOption', { name: s.name, minutes: s.duration_min })}
              </option>
            ))}
          </select>
          {showLengths ? (
            <select
              aria-label={t('clients.lengthWhich')}
              value={addingOption}
              onChange={(e) => setAddingOption(e.target.value)}
            >
              {addingSlots.map((optionId) =>
                optionId === null ? (
                  <option key="" value="">
                    {t('clients.lengthHowever')}
                  </option>
                ) : (
                  <option key={optionId} value={optionId}>
                    {(addingService?.options ?? []).find((o) => o.option_id === optionId)?.name ??
                      optionId}
                  </option>
                ),
              )}
            </select>
          ) : null}
          <input
            inputMode="numeric"
            placeholder={t('clients.lengthMinutes')}
            value={addMinutes}
            onChange={(e) => setAddMinutes(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <button
            type="button"
            className="button"
            disabled={!inRange(Number(addMinutes)) || adding === ''}
            onClick={() => {
              const service = services.find((s) => s.service_id === adding);
              if (service === undefined) return;
              const option = (service.options ?? []).find((o) => o.option_id === addingOption);
              void actions.setDuration({
                clientId: client.client_id,
                clientName: client.display_name,
                serviceId: service.service_id,
                // `''` is the broad row; anything else is one length's, and it
                // rides the intent's target onto the option door.
                ...(option === undefined ? {} : { optionId: option.option_id }),
                serviceName:
                  option === undefined
                    ? service.name
                    : t('clients.lengthOption', { service: service.name, option: option.name }),
                durationMin: Number(addMinutes),
              });
              setAdding('');
              setAddingOption('');
              setAddMinutes('');
            }}
          >
            {t('common.add')}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * One row's "make it N instead" — open on tap, so the list stays a list.
 * `optionId` is the row's: a correction on "Facial · 90 minutes" restates the
 * ninety's row, not the broad one.
 */
function LengthEditor({
  clientId,
  clientName,
  serviceId,
  optionId,
  serviceName,
  minutes,
}: {
  clientId: string;
  clientName: string;
  serviceId: string;
  optionId: string | null;
  serviceName: string;
  minutes: number;
}): ReactNode {
  const actions = useActions();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(minutes));

  if (!open) {
    return (
      <button
        type="button"
        className="link-button"
        onClick={() => {
          setValue(String(minutes));
          setOpen(true);
        }}
      >
        {t('common.edit')}
      </button>
    );
  }

  return (
    <>
      <input
        inputMode="numeric"
        aria-label={t('clients.lengthMinutes')}
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
      />
      <button
        type="button"
        className="button"
        disabled={!inRange(Number(value))}
        onClick={() => {
          void actions.setDuration({
            clientId,
            clientName,
            serviceId,
            ...(optionId === null ? {} : { optionId }),
            serviceName,
            durationMin: Number(value),
          });
          setOpen(false);
        }}
      >
        {t('common.save')}
      </button>
      <button type="button" className="link-button" onClick={() => setOpen(false)}>
        {t('common.cancel')}
      </button>
    </>
  );
}

/** The bounds `@yourstore/domain` states and the database CHECKs. One rule. */
function inRange(minutes: number): boolean {
  return (
    Number.isInteger(minutes) &&
    minutes >= CLIENT_DURATION_MIN_MINUTES &&
    minutes <= CLIENT_DURATION_MAX_MINUTES
  );
}

/**
 * **What she has bought** — treatments and products together (B3;
 * `docs/lovespa-discovery.md` §3: *"what each client has bought"*, third on her
 * own list).
 *
 * One read of `GET /v1/settlements?client_id=`, newest first, paged. Every line
 * of every document, so a facial and the cream she took home are one visit on
 * the screen rather than two lists to compare — which is exactly the question
 * being asked at the counter ("what did I give her last time?").
 *
 * ## Online, and it says so
 *
 * This is the `Reports` and `Orders` decision applied again (`useRemote`'s own
 * note): the mirror holds what the device *works on* and can queue writes
 * against, and there is nothing to queue here. A year of settlements is also
 * not a working set — it is history, it only grows, and mirroring it would put
 * the whole ledger on an iPad to answer a question asked a few times a day. So
 * it fetches, and offline it prints one sentence saying the history needs a
 * connection rather than showing an empty list that reads like "she has never
 * bought anything".
 *
 * **Read-only.** A correction to a settlement is a document, not an edit (§7.3),
 * and there is no verb for one here or anywhere else in this app.
 */
export function ClientPurchases({
  clientId,
  currency,
}: {
  clientId: string;
  currency: string;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const connection = useConnection();
  const [limit, setLimit] = useState(PURCHASE_PAGE);
  /**
   * **Refunding is started from here** (migration 0046) — the screen that
   * already answers "what did I give her last time?" is the screen somebody is
   * looking at when a guest brings a jar back.
   *
   * It is a sheet rather than an inline form because a refund needs the
   * server's view of what has already been given back, which is a read this
   * list does not make, and offline the sheet says so instead of offering a
   * form that cannot be honoured.
   */
  const [refunding, setRefunding] = useState<string | null>(null);

  const purchases = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/settlements',
        query: { client_id: clientId, limit },
        schema: SettlementList,
        signal,
      }),
    [clientId, limit],
    {
      // A settlement she is looking at is one somebody just rang up on the other
      // iPad, so the cue is worth having; the poll is slow because a purchase
      // history is not a pass being watched.
      cue: (type) => type === 'settlement.recorded',
      pollMs: 120_000,
    },
  );

  const rows = purchases.data?.settlements ?? [];

  if (purchases.data === null) {
    return (
      <>
        <h2>{t('clients.purchasesHeading')}</h2>
        <p className="muted">
          {connection === 'live' ? t('clients.purchasesLoading') : t('clients.purchasesOffline')}
        </p>
      </>
    );
  }

  /**
   * Totals by year, in the tenant's own calendar.
   *
   * `settled_on` is **the date of money** (§7.4 rule 1) and is already a
   * `YYYY-MM-DD` in the tenant's timezone, so the year is the first four
   * characters and no timezone decision is made here. `total_cents` is what the
   * document came to, which is the number she means by "what has she spent".
   */
  const byYear = new Map<string, number>();
  for (const row of rows) {
    const year = row.settled_on.slice(0, 4);
    byYear.set(year, (byYear.get(year) ?? 0) + row.total_cents);
  }

  return (
    <>
      <h2>{t('clients.purchasesHeading')}</h2>
      {connection === 'live' ? null : <p className="muted">{t('clients.purchasesStale')}</p>}

      {refunding === null ? null : (
        <RefundSheet
          settlementId={refunding}
          currency={currency}
          onCancel={() => setRefunding(null)}
          onDone={() => {
            setRefunding(null);
            // The list is the only place the new numbers can come from: the
            // refund is a separate document and `refunded_cents` is derived.
            purchases.reload();
          }}
        />
      )}

      {rows.length === 0 ? (
        <p className="muted">{t('clients.purchasesNone')}</p>
      ) : (
        <>
          <ul className="purchase-list">
            {rows.map((row) => (
              <li key={row.settlement_id}>
                <div className="purchase__head">
                  <strong>{formatDay(row.settled_on, locale)}</strong>
                  <span>{formatMoney(row.total_cents, currency, locale)}</span>
                </div>
                <ul className="purchase__lines">
                  {(row.lines ?? []).map((line) => (
                    <li key={line.line_id}>
                      <span>
                        {line.qty > 1 ? `${String(line.qty)} × ` : ''}
                        {line.item_name}
                      </span>
                      <span>{formatMoney(line.charged_cents, currency, locale)}</span>
                    </li>
                  ))}
                </ul>
                <small className="muted">
                  {/* The tender words are the server's closed set, printed as
                      the server names them — Domain C, not ours to paraphrase. */}
                  {row.tenders.map((tender) => tender.kind).join(' · ')}
                  {/* A tip is money for a person, so it is named rather than
                      folded into the total beside it (§7.4, 0046). */}
                  {row.tip_cents > 0
                    ? ` · ${t('clients.purchaseTip', {
                        amount: formatMoney(row.tip_cents, currency, locale),
                      })}`
                    : ''}
                </small>
                {/*
                  What came back off this sale — a line, never a strike-through
                  on the sale itself. The settlement is what it was; a screen
                  that rewrote it would teach the owner that a ledger is
                  editable (spec §7.6).
                */}
                {row.refunded_cents + row.refunded_tip_cents > 0 ? (
                  <small className="muted">
                    {t('clients.purchaseRefunded', {
                      amount: formatMoney(
                        row.refunded_cents + row.refunded_tip_cents,
                        currency,
                        locale,
                      ),
                    })}
                  </small>
                ) : null}
                {connection === 'live' && row.refunded_cents < row.total_cents ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setRefunding(row.settlement_id)}
                  >
                    {t('clients.refund')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>

          <ul className="purchase-years">
            {[...byYear.entries()]
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([year, cents]) => (
                <li key={year}>
                  <span>{year}</span>
                  <span>{formatMoney(cents, currency, locale)}</span>
                </li>
              ))}
          </ul>

          {/* A page, not a scroll to the beginning of time. The totals above
              are of what has been *loaded*, and the button says so by being
              there at all. */}
          {rows.length >= limit ? (
            <button
              type="button"
              className="button"
              onClick={() => setLimit((n) => n + PURCHASE_PAGE)}
            >
              {t('clients.purchasesMore')}
            </button>
          ) : null}
        </>
      )}
    </>
  );
}

const PURCHASE_PAGE = 20;

/** `YYYY-MM-DD` as a person reads it, in the device's language. */
function formatDay(isoDay: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${isoDay}T12:00:00Z`));
}

function AddIdentity({ client }: { client: ClientDetail }): ReactNode {
  const actions = useActions();
  const t = useT();
  const [value, setValue] = useState('');
  const [kind, setKind] = useState<WireIdentity['identity_kind']>('phone');

  return (
    <div className="editor editor--inline">
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value === 'email' ? 'email' : 'phone')}
      >
        <option value="phone">{t('clients.kindPhone')}</option>
        <option value="email">{t('clients.kindEmail')}</option>
      </select>
      <input
        placeholder={t('clients.addIdentity')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button
        type="button"
        className="button"
        disabled={value.trim() === ''}
        onClick={() => {
          void actions.addIdentity({
            clientId: client.client_id,
            name: client.display_name,
            identity: { identity_kind: kind, identity: value.trim() },
          });
          setValue('');
        }}
      >
        {t('common.add')}
      </button>
    </div>
  );
}

function NewClientSheet({ onClose }: { onClose: () => void }): ReactNode {
  const actions = useActions();
  const t = useT();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  return (
    <div className="sheet" role="dialog" aria-label={t('clients.newClient')}>
      <div className="sheet__panel">
        <h2>{t('clients.newClient')}</h2>
        <label className="field">
          <span>{t('common.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('clients.phone')}</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
        </label>
        <div className="sheet__actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={name.trim() === ''}
            onClick={() => {
              void actions.createClient({
                name: name.trim(),
                identities:
                  phone.trim() === '' ? [] : [{ identity_kind: 'phone', identity: phone.trim() }],
              });
              onClose();
            }}
          >
            {t('common.add')}
          </button>
        </div>
      </div>
    </div>
  );
}
